import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import IncomeResumeRow from './IncomeResumeRow';
import useStyles from './styles';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ incomeResumeList }) =>
  incomeResumeList.map(incomeResume => (
    <IncomeResumeRow key={incomeResume.id} {...incomeResume} />
  ));

const IncomeResumeTable = ({
  className,
  incomeResumeList,
  count,
  page,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const theads = useMemo(
    () => () => (
      <TableRow>
        <TableCell
          variant="head"
          key="date"
          sx={{ width: '10%' }}
          align="center"
        >
          {t('COMMON.DATE')}
        </TableCell>

        <TableCell
          variant="head"
          key="initialBalance"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.INITIAL_BALANCE')}
        </TableCell>

        <TableCell
          variant="head"
          key="initialPayment"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.INITIAL_PAYMENT')}
        </TableCell>

        <TableCell variant="head" key="rent" align="center">
          {t('INCOME_RESUME.RENT')}
        </TableCell>

        <TableCell
          variant="head"
          key="capitalContribution"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('COMMON.CONTRIBUTION')}
        </TableCell>

        <TableCell
          variant="head"
          key="residualPayment"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.RESIDUAL_PAYMENT')}
        </TableCell>

        <TableCell
          variant="head"
          key="interests"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.INTERESTS')}
        </TableCell>

        <TableCell
          variant="head"
          key="discounts"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.DISCOUNTS')}
        </TableCell>

        <TableCell
          variant="head"
          key="payments"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.PAYMENTS')}
        </TableCell>

        <TableCell
          variant="head"
          key="finalBalance"
          align="center"
          sx={{ width: '10%' }}
        >
          {t('INCOME_RESUME.FINAL_BALANCE')}
        </TableCell>
      </TableRow>
    ),
    [classes]
  );

  const tbody = useMemo(
    () => () => <Tbody incomeResumeList={incomeResumeList} />,
    [incomeResumeList]
  );

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, incomeResumeList]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

IncomeResumeTable.propTypes = {
  className: PropTypes.string,
  incomeResumeList: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool
};

IncomeResumeTable.defaultProps = {
  count: 0
};

export default IncomeResumeTable;
